import { useState, useEffect, useCallback } from 'react';

const DARK_MODE_KEY = '1world_dark_mode';

/**
 * Shared dark mode hook — persists to localStorage under a single key
 * so all pages stay in sync. Also listens to the `storage` event so
 * toggling on one page instantly reflects on any other open tab.
 */
export function useDarkMode(): [boolean, () => void] {
  const [darkMode, setDarkMode] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(DARK_MODE_KEY);
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  // Persist whenever value changes
  useEffect(() => {
    try {
      localStorage.setItem(DARK_MODE_KEY, JSON.stringify(darkMode));
    } catch { /* ignore */ }
  }, [darkMode]);

  // Sync across tabs / other page instances
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === DARK_MODE_KEY && e.newValue !== null) {
        try {
          setDarkMode(JSON.parse(e.newValue));
        } catch { /* ignore */ }
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const toggle = useCallback(() => {
    setDarkMode(prev => !prev);
  }, []);

  return [darkMode, toggle];
}
